import {
  BRAND_RADIUS_KEYS,
  BRAND_RADIUS_LABELS,
  type BrandBindingSlot,
  bindColor,
  bindRadius,
  bindTextStyle,
  brandColor,
  brandScale,
  buildParentMap,
  type CaptionsElement,
  type ChartElement,
  type ClipColor,
  clearMarkFields,
  closestWeight,
  type DeviceElement,
  docMmToPx,
  docPtToPx,
  docPxToMm,
  docPxToPt,
  getFontFamily,
  getPrintProvider,
  hasItalic,
  type IconElement,
  isImagePlaceholder,
  type MarkField,
  missingTokens,
  type PathElement,
  PRINT_PROFILES,
  PRINT_PROVIDERS,
  type QrElement,
  resolvePrintProfile,
  type SceneDocument,
  type SceneElement,
  SHAPE_KINDS,
  type ShapeKind,
  setWorldBox,
  storedColor,
  supportsBoard,
  type TableElement,
  type TextElement,
  type TextListKind,
  unbindElement,
  WEIGHT_LABELS,
  worldBox,
} from "@cva/shared";
import type { Draft } from "immer";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  ArrowDownToLine,
  ArrowUpToLine,
  Bold,
  BookmarkPlus,
  CaseUpper,
  ChevronDown,
  ChevronUp,
  Copy,
  Crop,
  ExternalLink,
  FlipHorizontal2,
  FlipVertical2,
  Group,
  ImagePlus,
  IndentDecrease,
  IndentIncrease,
  Italic,
  LayoutGrid,
  Link2,
  List,
  ListOrdered,
  Lock,
  LockOpen,
  Smartphone,
  Strikethrough,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignJustify,
  TextAlignStart,
  Trash2,
  Underline,
  Ungroup,
  Unlink,
} from "lucide-react";
import type { EditorView } from "prosemirror-view";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { MediaPickerDialog } from "../components/media-picker";
import { Select } from "../components/ui/input";
import { cn } from "../lib/utils";
import { PatternSection } from "./Backgrounds";
import { ChartProperties } from "./ChartProperties";
import { type BrandLink, ColorButton } from "./ColorPicker";
import { canGroup } from "./ContextMenu";
import { CutoutControl } from "./CutoutControl";
import {
  alignSelection,
  deleteSelection,
  distributeSelection,
  duplicateSelection,
  groupSelection,
  reorderSelection,
  replaceImage,
  toggleLock,
  ungroupSelection,
} from "./commands";
import { toAssetInfo, useEnv } from "./env";
import { FillButton } from "./FillPicker";
import { FontPicker } from "./FontPicker";
import { IconProperties } from "./IconLibrary";
import { ImagePalette } from "./ImagePalette";
import { LayoutSection } from "./LayoutPanel";
import { LineCapFields } from "./LineProperties";
import { MaskPicker } from "./MaskPicker";
import { DeviceProperties } from "./Mockups";
import { imageToDevice } from "./mockup-commands";
import { PageNotes } from "./PageNotes";
import { PageSizeSection } from "./PageSettings";
import { PathProperties } from "./PathProperties";
import { QrProperties } from "./QrProperties";
import {
  indentList,
  type RichContext,
  setField,
  toggleBold,
  toggleDecoration,
  toggleItalic,
  toggleList,
  useRichText,
  withRichText,
} from "./richtext";
import { requestSaveBlock } from "./SaveBlockDialog";
import { openShortcuts } from "./ShortcutsDialog";
import { useEditor, useEditorApi } from "./store";
import { TableProperties } from "./TableProperties";
import { TextEffectsSection } from "./TextEffects";
import { tidySelection } from "./tidy";
import { IconButton, NumberField, Row, Section, Segmented, Slider, Toggle } from "./ui";
import { CaptionInspector, CaptionsSummary } from "./video/Captions";
import { LookSection } from "./video/ColorSection";
import { videoIntoPlaceholder } from "./video/commands";
import { ClipProperties, TimeProperties, VideoSettings } from "./video/Properties";
import { useVideoApi, useVideoMaybe } from "./video/store";

const SHAPE_LABELS: Record<ShapeKind, string> = {
  rect: "Rechteck",
  ellipse: "Ellipse",
  triangle: "Dreieck",
  line: "Linie",
  polygon: "Vieleck",
  star: "Stern",
};

function useUnits() {
  const doc = useEditor((s) => s.doc);
  const print = Boolean(doc.format.print);
  return {
    print,
    unit: print ? "mm" : "px",
    precision: print ? 1 : 0,
    step: print ? 0.5 : 1,
    toDisplay: (px: number) => (print ? docPxToMm(doc, px) : px),
    fromDisplay: (value: number) => (print ? docMmToPx(doc, value) : value),
    fontUnit: print ? "pt" : "px",
    fontToDisplay: (px: number) => (print ? docPxToPt(doc, px) : px),
    fontFromDisplay: (value: number) => (print ? docPtToPx(doc, value) : value),
  };
}

/** Applies a change to every selected element (grouped into one undo step per field). */
function useUpdateSelected() {
  const store = useEditorApi();
  return (
    recipe: (element: Draft<SceneElement>, doc: Draft<SceneDocument>) => void,
    merge?: string,
  ) => {
    const { selection } = store.getState();
    store.getState().update(
      (draft) => {
        for (const id of selection) {
          const element = draft.elements[id];
          if (element) recipe(element, draft);
        }
      },
      merge ? { merge } : undefined,
    );
  };
}

function same<T>(values: T[]): { value: T | undefined; mixed: boolean } {
  const first = values[0];
  return { value: first, mixed: values.some((value) => value !== first) };
}

type ColorSlot = "fill" | "stroke" | "color" | "textStroke";

/** Link of a colour slot of the selection to a brand colour (for colour buttons). */
function useBrandLink(elements: SceneElement[], slot: ColorSlot): BrandLink | undefined {
  const store = useEditorApi();
  const brand = useEditor((s) => s.brand);
  if (!brand) return undefined;
  const token = same(elements.map((element) => element.brand?.[slot]));
  return {
    token: token.mixed ? undefined : token.value,
    onToken: (key) =>
      store.getState().update((draft) => {
        for (const element of elements) {
          const target = draft.elements[element.id];
          if (target) bindColor(target, slot, key, brand);
        }
      }),
  };
}

const SLOT_LABELS: Record<BrandBindingSlot, string> = {
  fill: "Füllung",
  stroke: "Kontur",
  color: "Farbe",
  textStroke: "Textkontur",
  text: "Textstil",
  radius: "Radius",
};

/** Shows which properties follow the brand kit, with "Vom Brand lösen". */
function BrandBindings({ elements }: { elements: SceneElement[] }) {
  const store = useEditorApi();
  const brand = useEditor((s) => s.brand);
  const { brandMissing } = useEnv();
  const bound = elements.filter(
    (element) => element.brand && Object.keys(element.brand).length > 0,
  );
  if (bound.length === 0) return null;
  const describe = (slot: BrandBindingSlot, key: string) => {
    if (slot === "text") return brand?.textStyles.find((style) => style.key === key)?.name ?? key;
    if (slot === "radius") return BRAND_RADIUS_LABELS[key as keyof typeof BRAND_RADIUS_LABELS];
    return brand?.colors.find((color) => color.key === key)?.name ?? key;
  };
  const single = elements.length === 1 ? elements[0]!.brand : undefined;
  const missing = brand ? bound.flatMap((element) => missingTokens(element, brand)) : [];
  return (
    <div className="border-b border-zinc-100 bg-brand-50/60 px-4 py-2.5 text-xs">
      <div className="flex items-start gap-2">
        <Link2 className="mt-0.5 size-3.5 shrink-0 text-brand-600" />
        <div className="min-w-0 flex-1 text-zinc-700">
          <p className="font-medium text-brand-800">Mit der Marke verknüpft</p>
          {single ? (
            <p className="mt-0.5 text-zinc-600">
              {(Object.entries(single) as [BrandBindingSlot, string][])
                .map(([slot, key]) => `${SLOT_LABELS[slot]}: ${describe(slot, key)}`)
                .join(" · ")}
            </p>
          ) : (
            <p className="mt-0.5 text-zinc-600">
              {bound.length} von {elements.length} Elementen
            </p>
          )}
          {brandMissing ? (
            <p className="mt-1 text-amber-700">
              Ohne Brand Kit bleiben die zuletzt übernommenen Werte erhalten.
            </p>
          ) : missing.length > 0 ? (
            <p className="mt-1 text-amber-700">
              Ein verknüpfter Wert fehlt im Brand Kit; der zuletzt bekannte Wert bleibt erhalten.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          title="Vom Brand lösen: Werte bleiben, folgen aber keinen Änderungen am Brand Kit mehr"
          onClick={() =>
            store.getState().update((draft) => {
              for (const element of bound) {
                const target = draft.elements[element.id];
                if (target) unbindElement(target);
              }
            })
          }
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 font-medium text-brand-700 hover:bg-brand-100"
        >
          <Unlink className="size-3.5" /> Lösen
        </button>
      </div>
    </div>
  );
}

/** Brand radius tokens as quick buttons below a radius field. */
function BrandRadiusChips({ elements }: { elements: SceneElement[] }) {
  const store = useEditorApi();
  const brand = useEditor((s) => s.brand);
  const scale = useEditor((s) => brandScale(s.doc));
  if (!brand) return null;
  const current = same(elements.map((element) => element.brand?.radius));
  return (
    <div className="flex flex-wrap gap-1">
      {BRAND_RADIUS_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          title={`Radius „${BRAND_RADIUS_LABELS[key]}“ aus der Marke`}
          onClick={() =>
            store.getState().update((draft) => {
              for (const element of elements) {
                const target = draft.elements[element.id];
                if (target) bindRadius(target, key, brand, scale);
              }
            })
          }
          className={cn(
            "rounded-md border px-2 py-0.5 text-[11px] text-zinc-600 hover:border-brand-300 hover:bg-brand-50",
            !current.mixed && current.value === key
              ? "border-brand-500 bg-brand-50 text-brand-700"
              : "border-zinc-200",
          )}
        >
          {BRAND_RADIUS_LABELS[key]}
        </button>
      ))}
    </div>
  );
}

export function PropertiesPanel() {
  const selection = useEditor((s) => s.selection);
  const doc = useEditor((s) => s.doc);
  const readOnly = useEditor((s) => s.readOnly);
  const elements = selection
    .map((id) => doc.elements[id])
    .filter((element): element is SceneElement => Boolean(element));

  if (readOnly) {
    return (
      <div className="p-4 text-sm text-zinc-500">
        Nur ansehen – Änderungen sind in diesem Modus nicht möglich.
      </div>
    );
  }
  if (elements.length === 0) {
    if (doc.kind === "video") return <VideoProperties />;
    return <PageProperties />;
  }
  const types = new Set(elements.map((element) => element.type));
  const only = types.size === 1 ? [...types][0] : null;
  if (only === "captions" && elements.length === 1) {
    return (
      <div>
        <SelectionHeader elements={elements} />
        <CaptionInspector
          element={elements[0] as CaptionsElement}
          styleExtras={{
            before: <BrandBindings elements={elements} />,
            after: <ArrangeProperties elements={elements} />,
          }}
        />
      </div>
    );
  }
  return (
    <div>
      <SelectionHeader elements={elements} />
      <GroupActions elements={elements} />
      <BrandBindings elements={elements} />
      {doc.kind === "video" ? <TimeProperties elements={elements} /> : null}
      {only === "text" ? (
        <TextProperties elements={elements as Extract<SceneElement, { type: "text" }>[]} />
      ) : null}
      {elements.length === 1 && isImagePlaceholder(elements[0]) ? (
        <ImageSource element={elements[0]!} placeholder />
      ) : null}
      {elements.length === 1 && isImagePlaceholder(elements[0]) ? (
        <Section title="Rahmenform">
          <MaskPicker element={elements[0]} />
        </Section>
      ) : null}
      {only === "shape" ? (
        <ShapeProperties elements={elements as Extract<SceneElement, { type: "shape" }>[]} />
      ) : null}
      {only === "shape" && elements.length === 1 && elements[0]?.type === "shape" ? (
        <PatternSection element={elements[0]} />
      ) : null}
      {only === "image" && elements.length === 1 ? (
        <>
          <ImageSource element={elements[0]!} />
          <ImageProperties element={elements[0] as Extract<SceneElement, { type: "image" }>} />
          <ImageColorSection element={elements[0] as Extract<SceneElement, { type: "image" }>} />
          <ImagePalette element={elements[0] as Extract<SceneElement, { type: "image" }>} />
        </>
      ) : null}
      {only === "table" && elements.length === 1 ? (
        <TableProperties element={elements[0] as TableElement} />
      ) : null}
      {only === "qr" && elements.length === 1 ? (
        <QrSection element={elements[0] as QrElement} />
      ) : null}
      {only === "icon" ? <IconSection elements={elements as IconElement[]} /> : null}
      {only === "device" && elements.length === 1 ? (
        <DeviceProperties key={elements[0]!.id} element={elements[0] as DeviceElement} />
      ) : null}
      {only === "path" ? <PathSection elements={elements as PathElement[]} /> : null}
      {only === "chart" && elements.length === 1 ? (
        <ChartSection element={elements[0] as ChartElement} />
      ) : null}
      {only === "svg" || only === "image" ? <FlipProperties /> : null}
      <ArrangeProperties elements={elements} />
      {only === "text" ? (
        <TextEffectsSection elements={elements as TextElement[]} />
      ) : only !== "group" ? (
        <EffectProperties elements={elements} />
      ) : null}
    </div>
  );
}

/**
 * Grouping and saving at a glance: several elements → one group that moves
 * (and in videos plays) as a whole; any selection → a reusable building block.
 */
function GroupActions({ elements }: { elements: SceneElement[] }) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const selection = useEditor((s) => s.selection);
  const groupable = canGroup(doc, selection);
  const group = elements.length === 1 && elements[0]!.type === "group";
  const button =
    "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border text-xs font-medium [&_svg]:size-3.5";
  if (!groupable && !group && elements.length !== 1) return null;
  return (
    <div className="space-y-1.5 border-b border-zinc-100 px-4 py-2.5">
      <Row>
        {groupable ? (
          <button
            type="button"
            onClick={() => groupSelection(store)}
            className={cn(button, "border-brand-600 bg-brand-600 text-white hover:bg-brand-700")}
            title="Die Elemente zu einer Gruppe zusammenfassen (⌘G): sie lassen sich dann gemeinsam verschieben"
          >
            <Group /> Gruppieren
          </button>
        ) : null}
        {group ? (
          <button
            type="button"
            onClick={() => ungroupSelection(store)}
            className={cn(button, "border-zinc-200 text-zinc-700 hover:bg-zinc-50")}
            title="Gruppierung aufheben (⇧⌘G)"
          >
            <Ungroup /> Auflösen
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => requestSaveBlock(selection)}
          className={cn(button, "border-zinc-200 text-zinc-700 hover:bg-zinc-50")}
          title="Für alle im Team unter „Bausteine → Meine Bausteine“ speichern"
        >
          <BookmarkPlus /> Als Baustein speichern
        </button>
      </Row>
      {group ? (
        <p className="text-[11px] text-zinc-500">
          Doppelklick auf die Gruppe wählt ein einzelnes Element darin aus.
        </p>
      ) : null}
    </div>
  );
}

function SelectionHeader({ elements }: { elements: SceneElement[] }) {
  const store = useEditorApi();
  const locked = elements.every((element) => element.locked);
  const groups = elements.some((element) => element.type === "group");
  const label =
    elements.length > 1
      ? `${elements.length} Elemente`
      : isImagePlaceholder(elements[0])
        ? "Bildplatzhalter"
        : elements[0]!.type === "shape"
          ? SHAPE_LABELS[(elements[0] as Extract<SceneElement, { type: "shape" }>).shape]
          : {
              text: "Text",
              image: "Bild",
              svg: "Grafik",
              qr: "QR-Code",
              table: "Tabelle",
              icon: "Icon",
              device: "Mockup",
              path: "Zeichnung",
              chart: "Diagramm",
              group: "Gruppe",
              shape: "Form",
              captions: "Untertitel",
            }[elements[0]!.type];
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
      <span className="text-sm font-semibold text-zinc-900">{label}</span>
      <div className="flex items-center">
        {elements.length > 1 ? (
          <IconButton label="Gruppieren" shortcut="⌘G" onClick={() => groupSelection(store)}>
            <Group />
          </IconButton>
        ) : null}
        {groups ? (
          <IconButton
            label="Gruppierung aufheben"
            shortcut="⇧⌘G"
            onClick={() => ungroupSelection(store)}
          >
            <Ungroup />
          </IconButton>
        ) : null}
        <IconButton
          label={locked ? "Entsperren" : "Sperren"}
          shortcut="⌘L"
          active={locked}
          onClick={() => toggleLock(store)}
        >
          {locked ? <Lock /> : <LockOpen />}
        </IconButton>
        <IconButton label="Duplizieren" shortcut="⌘D" onClick={() => duplicateSelection(store)}>
          <Copy />
        </IconButton>
        <IconButton label="Löschen" shortcut="⌫" onClick={() => deleteSelection(store)}>
          <Trash2 />
        </IconButton>
      </div>
    </div>
  );
}

function ArrangeProperties({ elements }: { elements: SceneElement[] }) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const units = useUnits();
  const update = useUpdateSelected();
  const single = elements.length === 1 ? elements[0]! : null;
  const box = single ? worldBox(doc, single.id, buildParentMap(doc)) : null;
  const opacity = same(elements.map((element) => element.opacity));
  const setBox = (
    patch: Partial<{ x: number; y: number; width: number; height: number; rotation: number }>,
    merge: string,
  ) => {
    if (!single || !box) return;
    store.getState().update(
      (draft) => {
        const next = { ...box, ...patch };
        const element = draft.elements[single.id];
        if (element?.type === "text" && patch.width !== undefined && element.autoHeight)
          next.height = box.height;
        setWorldBox(draft, single.id, next);
      },
      { merge },
    );
  };
  return (
    <Section title="Anordnen">
      {single && box ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              label="X"
              value={units.toDisplay(box.x)}
              unit={units.unit}
              precision={units.precision}
              step={units.step}
              onChange={(v) => setBox({ x: units.fromDisplay(v) }, "x")}
              disabled={single.locked}
            />
            <NumberField
              label="Y"
              value={units.toDisplay(box.y)}
              unit={units.unit}
              precision={units.precision}
              step={units.step}
              onChange={(v) => setBox({ y: units.fromDisplay(v) }, "y")}
              disabled={single.locked}
            />
            <NumberField
              label="B"
              value={units.toDisplay(box.width)}
              unit={units.unit}
              precision={units.precision}
              step={units.step}
              min={0.1}
              onChange={(v) => setBox({ width: Math.max(1, units.fromDisplay(v)) }, "w")}
              disabled={single.locked}
            />
            <NumberField
              label="H"
              value={units.toDisplay(box.height)}
              unit={units.unit}
              precision={units.precision}
              step={units.step}
              min={0.1}
              onChange={(v) => setBox({ height: Math.max(1, units.fromDisplay(v)) }, "h")}
              disabled={single.locked || (single.type === "text" && single.autoHeight)}
            />
          </div>
          <NumberField
            label="Drehung"
            value={box.rotation}
            unit="°"
            precision={1}
            onChange={(v) => setBox({ rotation: (((v % 360) + 540) % 360) - 180 }, "rot")}
            disabled={single.locked}
          />
        </>
      ) : null}
      <Row>
        <span className="w-20 shrink-0 text-xs text-zinc-500">Deckkraft</span>
        <Slider
          value={Math.round((opacity.value ?? 1) * 100)}
          min={0}
          max={100}
          onChange={(v) =>
            update((element) => {
              element.opacity = v / 100;
            }, "opacity")
          }
        />
        <span className="w-9 text-right text-xs text-zinc-500 tabular-nums">
          {Math.round((opacity.value ?? 1) * 100)} %
        </span>
      </Row>
      <div className="flex flex-wrap items-center gap-0.5">
        <IconButton label="Links ausrichten" onClick={() => alignSelection(store, "left")}>
          <AlignStartVertical />
        </IconButton>
        <IconButton label="Horizontal zentrieren" onClick={() => alignSelection(store, "center")}>
          <AlignCenterVertical />
        </IconButton>
        <IconButton label="Rechts ausrichten" onClick={() => alignSelection(store, "right")}>
          <AlignEndVertical />
        </IconButton>
        <IconButton label="Oben ausrichten" onClick={() => alignSelection(store, "top")}>
          <AlignStartHorizontal />
        </IconButton>
        <IconButton label="Vertikal zentrieren" onClick={() => alignSelection(store, "middle")}>
          <AlignCenterHorizontal />
        </IconButton>
        <IconButton label="Unten ausrichten" onClick={() => alignSelection(store, "bottom")}>
          <AlignEndHorizontal />
        </IconButton>
        {elements.length >= 3 ? (
          <>
            <IconButton
              label="Horizontal verteilen"
              onClick={() => distributeSelection(store, "x")}
            >
              <AlignHorizontalDistributeCenter />
            </IconButton>
            <IconButton label="Vertikal verteilen" onClick={() => distributeSelection(store, "y")}>
              <AlignVerticalDistributeCenter />
            </IconButton>
            <IconButton
              label="Aufräumen (gleichmäßiges Raster)"
              onClick={() => tidySelection(store)}
            >
              <LayoutGrid />
            </IconButton>
          </>
        ) : null}
      </div>
      <div className="flex items-center gap-0.5">
        <IconButton
          label="Ganz nach vorne"
          shortcut="⇧⌘]"
          onClick={() => reorderSelection(store, "front")}
        >
          <ArrowUpToLine />
        </IconButton>
        <IconButton
          label="Nach vorne"
          shortcut="⌘]"
          onClick={() => reorderSelection(store, "forward")}
        >
          <ChevronUp />
        </IconButton>
        <IconButton
          label="Nach hinten"
          shortcut="⌘["
          onClick={() => reorderSelection(store, "backward")}
        >
          <ChevronDown />
        </IconButton>
        <IconButton
          label="Ganz nach hinten"
          shortcut="⇧⌘["
          onClick={() => reorderSelection(store, "back")}
        >
          <ArrowDownToLine />
        </IconButton>
      </div>
    </Section>
  );
}

function TextProperties({ elements }: { elements: Extract<SceneElement, { type: "text" }>[] }) {
  const update = useUpdateSelected();
  const units = useUnits();
  const first = elements[0]!;
  const style = first.style;
  const brand = useEditor((s) => s.brand);
  const scale = useEditor((s) => brandScale(s.doc));
  // While a text is edited, typography applies to its selection (richtext.ts).
  const editingId = useRichText((s) => s.elementId);
  const selection = useRichText((s) => s.format);
  const rich = elements.length === 1 && editingId === first.id ? selection : null;
  const inText = (run: (view: EditorView, context: RichContext) => void, focus = true) =>
    withRichText(first.id, run, { focus });
  const family = rich?.fontFamily.value ?? style.fontFamily;
  const weight = rich?.fontWeight.value ?? style.fontWeight;
  const font = getFontFamily(family);
  const size = rich ? rich.fontSize : same(elements.map((element) => element.style.fontSize));
  /** Whole-text change: the element's value, marks of these fields removed. */
  const setStyle = (patch: Partial<typeof style>, merge?: string, fields: MarkField[] = []) =>
    update((element) => {
      if (element.type !== "text") return;
      Object.assign(element.style, patch);
      if (fields.length > 0) clearMarkFields(element as TextElement, fields);
    }, merge);
  const textStyle = same(elements.map((element) => element.brand?.text));
  const elementColorLink = useBrandLink(elements, "color");
  const colorLink: BrandLink | undefined =
    rich && brand
      ? {
          token: rich.colorToken.mixed ? undefined : rich.colorToken.value,
          onToken: (key) => {
            const color = brandColor(brand, key)?.color;
            if (color)
              inText((view, context) => setField(view, context, "color", color, key), false);
          },
        }
      : elementColorLink &&
        ({
          ...elementColorLink,
          onToken: (key) => {
            elementColorLink.onToken(key);
            update((element) => {
              if (element.type === "text") clearMarkFields(element as TextElement, ["color"]);
            });
          },
        } satisfies BrandLink);
  const strokeLink = useBrandLink(elements, "textStroke");
  const bold = rich ? !rich.fontWeight.mixed && weight >= 600 : style.fontWeight >= 700;
  const italic = rich ? !rich.italic.mixed && Boolean(rich.italic.value) : style.italic;
  const underline = rich
    ? !rich.underline.mixed && Boolean(rich.underline.value)
    : Boolean(style.underline);
  const strike = rich ? !rich.strike.mixed && Boolean(rich.strike.value) : Boolean(style.strike);
  const lists = elements.flatMap((element) => element.paragraphs ?? []);
  const listKind = rich
    ? rich.list.mixed
      ? null
      : rich.list.value
    : same(elements.map((element) => firstList(element))).value;
  const toggleListKind = (kind: TextListKind) => {
    if (inText((view) => toggleList(view, kind))) return;
    const all = elements.every((element) => everyParagraph(element, kind));
    update((element) => {
      if (element.type !== "text") return;
      const count = element.text.split("\n").length;
      if (all) delete element.paragraphs;
      else
        element.paragraphs = Array.from({ length: count }, (_, index) => ({
          list: kind,
          ...(element.paragraphs?.[index]?.indent
            ? { indent: element.paragraphs[index]!.indent }
            : {}),
        }));
    });
  };
  const hasList = rich ? rich.list.value !== null || rich.list.mixed : lists.some((f) => f?.list);
  return (
    <Section title="Text">
      {brand ? (
        <Select
          className="h-8"
          aria-label="Textstil der Marke"
          value={textStyle.mixed ? "__mixed" : (textStyle.value ?? "")}
          onChange={(event) => {
            const key = event.target.value;
            if (!key || key === "__mixed") return;
            update((element) => {
              bindTextStyle(element, key, brand, scale);
              if (element.type === "text") {
                clearMarkFields(element as TextElement, [
                  "fontFamily",
                  "fontSize",
                  "fontWeight",
                  "italic",
                ]);
              }
            });
          }}
        >
          {textStyle.mixed ? <option value="__mixed">Verschiedene Textstile</option> : null}
          <option value="">{textStyle.value ? "Textstil wählen" : "Kein Textstil (frei)"}</option>
          {brand.textStyles.map((entry) => (
            <option key={entry.key} value={entry.key}>
              Textstil: {entry.name}
            </option>
          ))}
        </Select>
      ) : null}
      {rich ? (
        <p className="text-[11px] leading-snug text-zinc-500">
          {rich.collapsed
            ? "Änderungen gelten ab der Schreibmarke."
            : "Änderungen gelten für den markierten Text."}
        </p>
      ) : null}
      <FontPicker
        value={rich?.fontFamily.mixed ? "" : family}
        onChange={(next) => {
          if (inText((view, context) => setField(view, context, "fontFamily", next))) return;
          update((element) => {
            if (element.type !== "text") return;
            element.style.fontFamily = next;
            element.style.fontWeight = closestWeight(next, element.style.fontWeight);
            if (element.style.italic && !hasItalic(next, element.style.fontWeight))
              element.style.italic = false;
            clearMarkFields(element as TextElement, ["fontFamily"]);
          });
        }}
      />
      <div className="grid grid-cols-[1fr_96px] gap-2">
        <Select
          className="h-8"
          value={rich?.fontWeight.mixed ? "__mixed" : weight}
          onChange={(event) => {
            if (event.target.value === "__mixed") return;
            const next = Number(event.target.value);
            if (inText((view, context) => setField(view, context, "fontWeight", next))) return;
            update((element) => {
              if (element.type !== "text") return;
              element.style.fontWeight = next;
              if (element.style.italic && !hasItalic(element.style.fontFamily, next))
                element.style.italic = false;
              clearMarkFields(element as TextElement, ["fontWeight"]);
            });
          }}
        >
          {rich?.fontWeight.mixed ? <option value="__mixed">Gemischt</option> : null}
          {(font?.weights ?? [400, 700]).map((entry) => (
            <option key={entry} value={entry}>
              {WEIGHT_LABELS[entry] ?? entry}
            </option>
          ))}
        </Select>
        <NumberField
          label="Aa"
          value={units.fontToDisplay(size.value ?? 16)}
          mixed={size.mixed}
          unit={units.fontUnit}
          precision={1}
          step={1}
          min={1}
          onChange={(v) => {
            const next = Math.max(1, units.fontFromDisplay(v));
            if (inText((view, context) => setField(view, context, "fontSize", next), false)) return;
            setStyle({ fontSize: next }, "fontSize", ["fontSize"]);
          }}
        />
      </div>
      <Row className="justify-between">
        <div className="flex items-center gap-0.5">
          <IconButton
            label="Fett"
            shortcut="⌘B"
            active={bold}
            onClick={() => {
              if (inText(toggleBold)) return;
              update((element) => {
                if (element.type !== "text") return;
                const next = element.style.fontWeight < 700;
                element.style.fontWeight = closestWeight(
                  element.style.fontFamily,
                  next ? 700 : 400,
                );
                clearMarkFields(element as TextElement, ["fontWeight"]);
              });
            }}
          >
            <Bold />
          </IconButton>
          <IconButton
            label={
              hasItalic(family, weight) ? "Kursiv" : "Kursiv (für diese Schrift nicht verfügbar)"
            }
            shortcut="⌘I"
            active={italic}
            disabled={!hasItalic(family, weight)}
            onClick={() => {
              if (inText(toggleItalic)) return;
              setStyle({ italic: !style.italic }, undefined, ["italic"]);
            }}
          >
            <Italic />
          </IconButton>
          <IconButton
            label="Unterstrichen"
            shortcut="⌘U"
            active={underline}
            onClick={() => {
              if (inText((view, context) => toggleDecoration(view, context, "underline"))) return;
              setStyle({ underline: !style.underline }, undefined, ["underline"]);
            }}
          >
            <Underline />
          </IconButton>
          <IconButton
            label="Durchgestrichen"
            shortcut="⇧⌘X"
            active={strike}
            onClick={() => {
              if (inText((view, context) => toggleDecoration(view, context, "strike"))) return;
              setStyle({ strike: !style.strike }, undefined, ["strike"]);
            }}
          >
            <Strikethrough />
          </IconButton>
          <IconButton
            label="Großbuchstaben"
            active={style.uppercase}
            onClick={() => setStyle({ uppercase: !style.uppercase })}
          >
            <CaseUpper />
          </IconButton>
        </div>
        <ColorButton
          value={rich?.color.value ?? style.color}
          onChange={(color) => {
            if (inText((view, context) => setField(view, context, "color", color, null), false))
              return;
            setStyle({ color }, undefined, ["color"]);
          }}
          label="Textfarbe"
          brandLink={colorLink}
        >
          <span className="text-zinc-600">{rich?.color.mixed ? "Gemischt" : "Farbe"}</span>
        </ColorButton>
      </Row>
      <Row className="justify-between">
        <div className="flex items-center gap-0.5">
          <IconButton
            label="Aufzählung"
            shortcut="⇧⌘8"
            active={listKind === "bullet"}
            onClick={() => toggleListKind("bullet")}
          >
            <List />
          </IconButton>
          <IconButton
            label="Nummerierte Liste"
            shortcut="⇧⌘7"
            active={listKind === "number"}
            onClick={() => toggleListKind("number")}
          >
            <ListOrdered />
          </IconButton>
        </div>
        {rich && hasList ? (
          <div className="flex items-center gap-0.5">
            <IconButton
              label="Einzug verkleinern"
              shortcut="⇧⇥"
              onClick={() => inText((view) => indentList(view, -1))}
            >
              <IndentDecrease />
            </IconButton>
            <IconButton
              label="Einzug vergrößern"
              shortcut="⇥"
              onClick={() => inText((view) => indentList(view, 1))}
            >
              <IndentIncrease />
            </IconButton>
          </div>
        ) : null}
      </Row>
      <Row>
        <Segmented
          value={style.align}
          onChange={(align) => setStyle({ align })}
          options={[
            { value: "left", label: <TextAlignStart />, title: "Linksbündig" },
            { value: "center", label: <TextAlignCenter />, title: "Zentriert" },
            { value: "right", label: <TextAlignEnd />, title: "Rechtsbündig" },
            { value: "justify", label: <TextAlignJustify />, title: "Blocksatz" },
          ]}
        />
        <Segmented
          value={style.verticalAlign}
          onChange={(verticalAlign) => setStyle({ verticalAlign })}
          options={[
            { value: "top", label: <AlignVerticalJustifyStart />, title: "Oben" },
            { value: "middle", label: <AlignVerticalJustifyCenter />, title: "Mitte" },
            { value: "bottom", label: <AlignVerticalJustifyEnd />, title: "Unten" },
          ]}
        />
      </Row>
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="Zeile"
          value={style.lineHeight}
          precision={2}
          step={0.05}
          min={0.5}
          max={5}
          onChange={(v) => setStyle({ lineHeight: v }, "lineHeight")}
        />
        <NumberField
          label="Abstand"
          value={style.letterSpacing * 100}
          precision={0}
          step={1}
          min={-50}
          max={200}
          unit="%"
          onChange={(v) => setStyle({ letterSpacing: v / 100 }, "letterSpacing")}
        />
      </div>
      <Toggle
        checked={Boolean(style.stroke)}
        onChange={(checked) =>
          update((element) => {
            if (element.type !== "text") return;
            element.style.stroke = checked
              ? { color: "#ffffff", width: Math.max(1, element.style.fontSize * 0.04) }
              : undefined;
          })
        }
        label="Kontur"
      />
      {style.stroke ? (
        <Row>
          <ColorButton
            value={style.stroke.color}
            brandLink={strokeLink}
            onChange={(color) =>
              update((element) => {
                if (element.type === "text" && element.style.stroke)
                  element.style.stroke.color = color;
              })
            }
          />
          <NumberField
            label="Stärke"
            value={units.fontToDisplay(style.stroke.width)}
            unit={units.fontUnit}
            precision={1}
            step={0.5}
            min={0}
            onChange={(v) =>
              update((element) => {
                if (element.type === "text" && element.style.stroke)
                  element.style.stroke.width = units.fontFromDisplay(v);
              }, "textStroke")
            }
          />
        </Row>
      ) : null}
    </Section>
  );
}

/** List kind of the first paragraph (null: none). */
function firstList(element: TextElement): TextListKind | null {
  return element.paragraphs?.[0]?.list ?? null;
}

/** Every paragraph of the text is an item of a `kind` list. */
function everyParagraph(element: TextElement, kind: TextListKind): boolean {
  const count = element.text.split("\n").length;
  return Array.from({ length: count }, (_, index) => element.paragraphs?.[index]?.list).every(
    (list) => list === kind,
  );
}

function StrokeFields({
  elements,
}: {
  elements: (Extract<SceneElement, { type: "shape" }> | Extract<SceneElement, { type: "image" }>)[];
}) {
  const update = useUpdateSelected();
  const units = useUnits();
  const first = elements[0]!;
  const stroke = first.stroke;
  const isLine = first.type === "shape" && first.shape === "line";
  const strokeLink = useBrandLink(elements, "stroke");
  return (
    <>
      {!isLine ? (
        <Toggle
          checked={Boolean(stroke && stroke.width > 0)}
          onChange={(checked) =>
            update((element) => {
              if (element.type !== "shape" && element.type !== "image") return;
              element.stroke = checked
                ? {
                    color: "#111111",
                    width: Math.max(1, Math.round(Math.min(element.width, element.height) * 0.02)),
                    dash: "solid",
                  }
                : undefined;
            })
          }
          label={first.type === "image" ? "Rahmen" : "Kontur"}
        />
      ) : null}
      {stroke ? (
        <>
          <Row>
            <ColorButton
              value={stroke.color}
              brandLink={strokeLink}
              onChange={(color) =>
                update((element) => {
                  if ((element.type === "shape" || element.type === "image") && element.stroke)
                    element.stroke.color = color;
                })
              }
            />
            <NumberField
              label="Stärke"
              value={units.fontToDisplay(stroke.width)}
              unit={units.fontUnit}
              precision={units.print ? 2 : 0}
              step={units.print ? 0.25 : 1}
              min={0}
              onChange={(v) =>
                update((element) => {
                  if ((element.type === "shape" || element.type === "image") && element.stroke)
                    element.stroke.width = units.fontFromDisplay(v);
                }, "strokeWidth")
              }
            />
          </Row>
          <Segmented
            value={stroke.dash}
            onChange={(dash) =>
              update((element) => {
                if ((element.type === "shape" || element.type === "image") && element.stroke)
                  element.stroke.dash = dash;
              })
            }
            options={[
              { value: "solid", label: "—", title: "Durchgezogen" },
              { value: "dashed", label: "- -", title: "Gestrichelt" },
              { value: "dotted", label: "···", title: "Gepunktet" },
            ]}
          />
        </>
      ) : null}
    </>
  );
}

function ShapeProperties({ elements }: { elements: Extract<SceneElement, { type: "shape" }>[] }) {
  const update = useUpdateSelected();
  const units = useUnits();
  const first = elements[0]!;
  const isLine = first.shape === "line";
  const fillLink = useBrandLink(elements, "fill");
  return (
    <Section title="Form">
      {!isLine ? (
        <Row>
          <span className="w-20 shrink-0 text-xs text-zinc-500">Füllung</span>
          <FillButton
            brandLink={fillLink}
            value={first.fill}
            onChange={(fill) =>
              update((element) => {
                if (element.type === "shape") element.fill = fill;
              })
            }
          />
        </Row>
      ) : null}
      {elements.length === 1 && !isLine ? (
        <Select
          className="h-8"
          value={first.shape}
          onChange={(event) =>
            update((element) => {
              if (element.type === "shape") element.shape = event.target.value as ShapeKind;
            })
          }
        >
          {SHAPE_KINDS.filter((kind) => kind !== "line").map((kind) => (
            <option key={kind} value={kind}>
              {SHAPE_LABELS[kind]}
            </option>
          ))}
        </Select>
      ) : null}
      {first.shape === "rect" ? (
        <NumberField
          label="Radius"
          value={units.toDisplay(first.radius)}
          unit={units.unit}
          precision={units.precision}
          step={units.step}
          min={0}
          onChange={(v) =>
            update((element) => {
              if (element.type === "shape") element.radius = Math.max(0, units.fromDisplay(v));
            }, "radius")
          }
        />
      ) : null}
      {first.shape === "rect" && elements.every((element) => element.shape === "rect") ? (
        <BrandRadiusChips elements={elements} />
      ) : null}
      {first.shape === "polygon" || first.shape === "star" ? (
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label={first.shape === "star" ? "Zacken" : "Ecken"}
            value={first.sides}
            min={3}
            max={24}
            onChange={(v) =>
              update((element) => {
                if (element.type === "shape")
                  element.sides = Math.round(Math.max(3, Math.min(24, v)));
              }, "sides")
            }
          />
          {first.shape === "star" ? (
            <NumberField
              label="Innen"
              value={first.innerRatio * 100}
              unit="%"
              min={10}
              max={95}
              onChange={(v) =>
                update((element) => {
                  if (element.type === "shape")
                    element.innerRatio = Math.max(0.1, Math.min(0.95, v / 100));
                }, "inner")
              }
            />
          ) : null}
        </div>
      ) : null}
      <StrokeFields elements={elements} />
      {elements.every((element) => element.shape === "line") ? (
        <LineCapFields elements={elements} />
      ) : null}
    </Section>
  );
}

/** Pick an image for a placeholder or replace the image of an image element. */
function ImageSource({ element, placeholder }: { element: SceneElement; placeholder?: boolean }) {
  const store = useEditorApi();
  const env = useEnv();
  const videoApi = useVideoApi();
  // In videos a placeholder also takes a video (a clip in its place, e.g. the speaker's circle).
  const takesVideo = Boolean(placeholder && videoApi);
  const [picking, setPicking] = useState(false);
  const button = (
    <button
      type="button"
      onClick={() => setPicking(true)}
      disabled={element.locked}
      className={cn(
        "inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md text-sm font-medium disabled:opacity-50",
        placeholder
          ? "bg-brand-600 text-white hover:bg-brand-700"
          : "border border-zinc-200 text-zinc-700 hover:bg-zinc-50",
      )}
    >
      <ImagePlus className="size-4" />{" "}
      {takesVideo ? "Bild oder Video wählen" : placeholder ? "Bild auswählen" : "Bild ersetzen"}
    </button>
  );
  const dialog = picking ? (
    <MediaPickerDialog
      orgId={env.orgId}
      open
      onOpenChange={setPicking}
      title={placeholder ? "Bild für den Platzhalter" : "Bild ersetzen"}
      description={
        takesVideo
          ? "Ein Bild füllt die Fläche aus; ein Video läuft als Clip in ihrer Form (rund bleibt rund)."
          : "Das Bild füllt die Fläche aus; den Ausschnitt legst du danach mit „Zuschneiden“ fest."
      }
      {...(takesVideo ? { kinds: ["image", "video"] } : {})}
      onPick={([asset]) => {
        setPicking(false);
        if (takesVideo && videoApi && asset?.kind === "video") {
          videoIntoPlaceholder(store, videoApi, element.id, toAssetInfo(asset));
          return;
        }
        if (asset?.kind !== "image") return;
        replaceImage(store, element.id, asset.id);
        store.getState().select([element.id]);
      }}
    />
  ) : null;
  if (!placeholder) {
    return (
      <div className="space-y-2 border-b border-zinc-100 px-4 py-3">
        {button}
        {element.type === "image" ? (
          <>
            <button
              type="button"
              disabled={element.locked}
              onClick={() => {
                const asset = env.assets.get(element.assetId);
                imageToDevice(store, element.id, {
                  id: element.assetId,
                  width: asset?.width ?? null,
                  height: asset?.height ?? null,
                });
              }}
              title="Das Bild als Screenshot in einem passenden Gerät zeigen (iPhone, MacBook, Browser …)"
              className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              <Smartphone className="size-4" /> Als Mockup zeigen
            </button>
            <CutoutControl
              target={{ kind: "element", id: element.id }}
              assetId={element.assetId}
              disabled={element.locked}
            />
          </>
        ) : null}
        {dialog}
      </div>
    );
  }
  return (
    <Section title="Bildplatzhalter">
      <p className="text-xs text-zinc-500">
        Ein Bild aus der Mediathek oder vom Computer auf die Fläche ziehen – oder hier auswählen.
      </p>
      {button}
      {dialog}
    </Section>
  );
}

function ImageProperties({ element }: { element: Extract<SceneElement, { type: "image" }> }) {
  const store = useEditorApi();
  const update = useUpdateSelected();
  const units = useUnits();
  return (
    <Section title="Bild">
      <Row>
        <button
          type="button"
          onClick={() => store.getState().setCropping(element.id)}
          className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50"
        >
          <Crop className="size-4" /> Zuschneiden
        </button>
        {element.crop ? (
          <button
            type="button"
            onClick={() =>
              update((target) => {
                if (target.type === "image") target.crop = null;
              })
            }
            className="h-8 rounded-md px-2 text-sm text-zinc-600 hover:bg-zinc-100"
          >
            Zurücksetzen
          </button>
        ) : null}
      </Row>
      {!element.crop ? (
        <Segmented
          className="w-full"
          value={element.fit}
          onChange={(fit) =>
            update((target) => {
              if (target.type === "image") target.fit = fit;
            })
          }
          options={[
            { value: "cover", label: "Füllen", title: "Rahmen ausfüllen" },
            { value: "contain", label: "Einpassen", title: "Ganzes Bild zeigen" },
          ]}
        />
      ) : null}
      <MaskPicker element={element} />
      {element.mask ? null : (
        <>
          <NumberField
            label="Ecken"
            value={units.toDisplay(element.radius)}
            unit={units.unit}
            precision={units.precision}
            step={units.step}
            min={0}
            onChange={(v) =>
              update((target) => {
                if (target.type === "image") target.radius = Math.max(0, units.fromDisplay(v));
              }, "radius")
            }
          />
          <BrandRadiusChips elements={[element]} />
        </>
      )}
      <StrokeFields elements={[element]} />
    </Section>
  );
}

/** Filters and adjustments of an image: the looks and sliders of video clips. */
function ImageColorSection({ element }: { element: Extract<SceneElement, { type: "image" }> }) {
  const store = useEditorApi();
  const env = useEnv();
  const asset = env.assets.get(element.assetId);
  const update = (next: ClipColor | undefined, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target?.type !== "image") return;
        const value = storedColor(next);
        if (value) target.color = value;
        else delete target.color;
      },
      merge ? { merge: `image-color-${element.id}-${merge}` } : undefined,
    );
  const applyEverywhere = () => {
    let count = 0;
    store.getState().update((draft) => {
      const value = storedColor(element.color);
      for (const target of Object.values(draft.elements)) {
        if (target.type !== "image" || target.locked) continue;
        if (value) target.color = structuredClone(value);
        else delete target.color;
        count++;
      }
    });
    toast.success(`Look auf ${count} ${count === 1 ? "Bild" : "Bilder"} übertragen`);
  };
  return (
    <LookSection
      title="Filter & Anpassungen"
      color={element.color}
      thumbnail={asset?.thumbnailUrl ?? asset?.url ?? null}
      onChange={update}
      applyAll={{
        label: "Auf alle Bilder",
        title: "Denselben Look für alle Bilder der Datei",
        run: applyEverywhere,
      }}
    />
  );
}

function QrSection({ element }: { element: QrElement }) {
  const colorLink = useBrandLink([element], "color");
  const backgroundLink = useBrandLink([element], "fill");
  return (
    <QrProperties
      key={element.id}
      element={element}
      colorLink={colorLink}
      backgroundLink={backgroundLink}
    />
  );
}

function ChartSection({ element }: { element: ChartElement }) {
  const colorLink = useBrandLink([element], "color");
  return <ChartProperties element={element} colorLink={colorLink} />;
}

function IconSection({ elements }: { elements: IconElement[] }) {
  const colorLink = useBrandLink(elements, "color");
  return <IconProperties elements={elements} colorLink={colorLink} />;
}

function PathSection({ elements }: { elements: PathElement[] }) {
  const colorLink = useBrandLink(elements, "color");
  return <PathProperties elements={elements} colorLink={colorLink} />;
}

function FlipProperties() {
  const update = useUpdateSelected();
  return (
    <Section title="Spiegeln">
      <Row>
        <IconButton
          label="Horizontal spiegeln"
          onClick={() =>
            update((element) => {
              if (element.type === "image" || element.type === "svg")
                element.flipX = !element.flipX;
            })
          }
        >
          <FlipHorizontal2 />
        </IconButton>
        <IconButton
          label="Vertikal spiegeln"
          onClick={() =>
            update((element) => {
              if (element.type === "image" || element.type === "svg")
                element.flipY = !element.flipY;
            })
          }
        >
          <FlipVertical2 />
        </IconButton>
      </Row>
    </Section>
  );
}

function EffectProperties({ elements }: { elements: SceneElement[] }) {
  const update = useUpdateSelected();
  const first = elements[0]!;
  const shadow = first.shadow;
  return (
    <Section title="Effekte">
      <Toggle
        checked={Boolean(shadow)}
        onChange={(checked) =>
          update((element) => {
            const size = Math.min(element.width, element.height);
            element.shadow = checked
              ? {
                  x: 0,
                  y: Math.round(size * 0.04) + 2,
                  blur: Math.round(size * 0.12) + 4,
                  color: "#00000040",
                }
              : undefined;
          })
        }
        label="Schatten"
      />
      {shadow ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            <NumberField
              label="X"
              value={shadow.x}
              onChange={(v) =>
                update((element) => {
                  if (element.shadow) element.shadow.x = v;
                }, "shadowX")
              }
            />
            <NumberField
              label="Y"
              value={shadow.y}
              onChange={(v) =>
                update((element) => {
                  if (element.shadow) element.shadow.y = v;
                }, "shadowY")
              }
            />
            <NumberField
              label="Weich"
              value={shadow.blur}
              min={0}
              onChange={(v) =>
                update((element) => {
                  if (element.shadow) element.shadow.blur = Math.max(0, v);
                }, "shadowBlur")
              }
            />
          </div>
          <ColorButton
            value={shadow.color}
            onChange={(color) =>
              update((element) => {
                if (element.shadow) element.shadow.color = color;
              })
            }
          />
        </>
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Nothing selected: page and document settings
// ---------------------------------------------------------------------------

/** Video documents without element selection: the selected clip, else the video settings. */
function VideoProperties() {
  const selectedClip = useVideoMaybe((s) => s.selectedClip, null);
  if (selectedClip) return <ClipProperties />;
  return (
    <div>
      <VideoSettings />
      <CaptionsSummary />
      <PageProperties video />
    </div>
  );
}

function PageProperties({ video = false }: { video?: boolean }) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const page = doc.pages[pageIndex];
  const brand = useEditor((s) => s.brand);
  if (!page) return null;
  const pageLink: BrandLink | undefined = brand
    ? {
        token: page.brand?.background,
        onToken: (key) =>
          store.getState().update((draft) => {
            const target = draft.pages[pageIndex];
            const color = brand.colors.find((entry) => entry.key === key)?.color;
            if (target && color) {
              target.background = color;
              target.brand = { background: key };
            }
          }),
      }
    : undefined;
  return (
    <div>
      <Section title={doc.pages.length > 1 ? `Seite ${pageIndex + 1}` : "Seite"}>
        <Row>
          <span className="w-20 shrink-0 text-xs text-zinc-500">Hintergrund</span>
          <FillButton
            value={page.background}
            brandLink={pageLink}
            onChange={(background) =>
              store.getState().update((draft) => {
                const target = draft.pages[pageIndex];
                if (target) target.background = background;
              })
            }
          />
        </Row>
        {video ? (
          <p className="text-xs text-zinc-500">Sichtbar, wo das Video den Rahmen nicht füllt.</p>
        ) : supportsBoard(doc) ? null : (
          <p className="text-xs text-zinc-500">
            {doc.format.print
              ? `${doc.format.print.widthMm} × ${doc.format.print.heightMm} mm`
              : `${doc.format.width} × ${doc.format.height} px`}
          </p>
        )}
        {video ? null : <PageNotes />}
      </Section>
      {video ? null : <PageSizeSection />}
      {video ? null : <LayoutSection />}
      {doc.format.print ? <PrintSettings /> : null}
      <Shortcuts video={video} />
    </div>
  );
}

export function PrintSettings({ compact }: { compact?: boolean }) {
  const store = useEditorApi();
  const print = useEditor((s) => s.doc.format.print);
  if (!print) return null;
  const provider = getPrintProvider(print.provider);
  const profile = resolvePrintProfile(print);
  const set = (patch: Partial<typeof print>, merge?: string) =>
    store.getState().update(
      (draft) => {
        if (draft.format.print) Object.assign(draft.format.print, patch);
      },
      merge ? { merge } : undefined,
    );
  return (
    <Section title={compact ? undefined : "Druck"}>
      <label className="block space-y-1">
        <span className="text-xs text-zinc-500">Druckerei</span>
        <Select
          className="h-8"
          value={provider.id}
          onChange={(event) => {
            const next = getPrintProvider(event.target.value);
            set({
              provider: next.id,
              bleedMm: next.bleedMm,
              safeMm: next.safeMm,
              profile: undefined,
            });
          }}
        >
          {PRINT_PROVIDERS.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </Select>
      </label>
      <Segmented
        className="w-full"
        value={print.paper ?? "coated"}
        onChange={(paper) => set({ paper, profile: undefined })}
        options={[
          { value: "coated", label: "Gestrichen", title: "Glänzendes/mattes Bilderdruckpapier" },
          {
            value: "uncoated",
            label: "Ungestrichen",
            title: "Offset-, Natur- oder Recyclingpapier",
          },
        ]}
      />
      <label className="block space-y-1">
        <span className="text-xs text-zinc-500">Farbprofil</span>
        <Select
          className="h-8"
          value={profile.id}
          onChange={(event) => set({ profile: event.target.value })}
        >
          {PRINT_PROFILES.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </Select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="Beschnitt"
          value={print.bleedMm}
          unit="mm"
          precision={1}
          step={0.5}
          min={0}
          max={30}
          onChange={(v) => set({ bleedMm: v }, "bleed")}
        />
        <NumberField
          label="Rand"
          value={print.safeMm}
          unit="mm"
          precision={1}
          step={0.5}
          min={0}
          max={50}
          onChange={(v) => set({ safeMm: v }, "safe")}
        />
      </div>
      {provider.notes.length > 0 ? (
        <ul className="space-y-1 text-xs text-zinc-500">
          {provider.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
      {provider.url ? (
        <a
          href={provider.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
        >
          Druckdaten-Vorgaben von {provider.name} <ExternalLink className="size-3" />
        </a>
      ) : null}
      <p className="text-xs text-zinc-400">
        Rote Linie: Endformat · Blau gestrichelt: Sicherheitsabstand · Heller Rand: Beschnitt (wird
        abgeschnitten)
      </p>
    </Section>
  );
}

function Shortcuts({ video = false }: { video?: boolean }) {
  const videoItems: [string, ReactNode][] = [
    ["Abspielen / Pause", "Leertaste"],
    ["Teilen am Abspielkopf", "S"],
    ["Bild vor / zurück", "← / →"],
    ["1 Sekunde vor / zurück", "⇧ ← / →"],
    ["Clip löschen", "⌫"],
  ];
  const items: [string, ReactNode][] = [
    ...(video ? videoItems : []),
    ["Text einfügen", "T"],
    ["Rechteck / Ellipse / Linie", "R / O / L"],
    ["Zeichnen (Esc beendet)", "P"],
    ["Verschieben ohne Einrasten", "⌘ halten"],
    ...(video ? [] : ([["Ansicht verschieben", "Leertaste + Ziehen"]] as [string, ReactNode][])),
    ["Zoomen", "⌘ + Scrollen"],
    ...(video
      ? []
      : ([["Lineale / Hilfslinien / Raster", "⇧R / ⇧H / ⇧G"]] as [string, ReactNode][])),
    ["Rückgängig / Wiederholen", "⌘Z / ⇧⌘Z"],
  ];
  return (
    <Section title="Tastenkürzel">
      <dl className="space-y-1 text-xs">
        {items.map(([label, keys]) => (
          <div key={label} className="flex justify-between gap-2">
            <dt className="text-zinc-500">{label}</dt>
            <dd className="font-mono text-zinc-700">{keys}</dd>
          </div>
        ))}
      </dl>
      <button
        type="button"
        onClick={openShortcuts}
        className="text-xs font-medium text-brand-700 hover:underline"
      >
        Alle Tastenkürzel anzeigen (?)
      </button>
    </Section>
  );
}
